/**
 * Design Tokens — WhiteLabelClaw Design System
 * 
 * ARQUITETURA DE 3 CAMADAS (padrão Google M3 / Adobe Spectrum / IBM Carbon):
 * 
 * ┌─────────────────────────────────────────────────────┐
 * │ CAMADA 1: PRIMITIVOS (Global)                       │
 * │ Paleta base: cores, escalas de fonte, espaçamentos  │
 * │ Ex: --p-blue-500, --p-font-14, --p-space-16        │
 * ├─────────────────────────────────────────────────────┤
 * │ CAMADA 2: SEMÂNTICOS (Alias)                        │
 * │ Significado: bg, text, primary, danger, etc          │
 * │ Ex: --t-bg, --t-text, --t-primary                   │
 * │ Referencia primitivos → troca de tema aqui           │
 * ├─────────────────────────────────────────────────────┤
 * │ CAMADA 3: COMPONENTE                                │
 * │ Tokens específicos por componente                    │
 * │ Ex: --c-button-bg, --c-card-radius                   │
 * │ Referencia semânticos → customização cirúrgica       │
 * └─────────────────────────────────────────────────────┘
 * 
 * Todas as camadas são CSS Custom Properties no :root.
 * Troca de tema = atualizar :root vars → tudo muda instantâneo.
 * 
 * USO: import { T } from '../styles/tokens';
 *      <div style={{ background: T.bg, color: T.text }}>
 */

import { themes, defaultTheme, type ThemeColors } from './themes';

// ============================================================
// CAMADA 1: TOKENS PRIMITIVOS (paleta base)
// Não mudam por tema — são a paleta completa disponível
// ============================================================

const PRIMITIVES_CSS = `
  /* Cores — Paleta Neutra */
  --p-white: #FFFFFF;
  --p-black: #000000;
  --p-neutral-50: #FAFAFA;
  --p-neutral-100: #F5F5F5;
  --p-neutral-200: #E5E5E5;
  --p-neutral-300: #D4D4D4;
  --p-neutral-400: #A3A3A3;
  --p-neutral-500: #737373;
  --p-neutral-600: #525252;
  --p-neutral-700: #404040;
  --p-neutral-800: #262626;
  --p-neutral-900: #171717;
  --p-neutral-950: #0A0A0A;

  /* Cores — Paleta Blue */
  --p-blue-50: #EFF6FF;
  --p-blue-100: #DBEAFE;
  --p-blue-200: #BFDBFE;
  --p-blue-300: #93C5FD;
  --p-blue-400: #60A5FA;
  --p-blue-500: #3B82F6;
  --p-blue-600: #2563EB;
  --p-blue-700: #1D4ED8;
  --p-blue-800: #1E40AF;
  --p-blue-900: #1E3A8A;

  /* Cores — Paleta Red */
  --p-red-50: #FEF2F2;
  --p-red-100: #FEE2E2;
  --p-red-200: #FECACA;
  --p-red-300: #FCA5A5;
  --p-red-400: #F87171;
  --p-red-500: #EF4444;
  --p-red-600: #DC2626;
  --p-red-700: #B91C1C;

  /* Cores — Paleta Green */
  --p-green-50: #ECFDF5;
  --p-green-100: #D1FAE5;
  --p-green-200: #A7F3D0;
  --p-green-300: #6EE7B7;
  --p-green-400: #34D399;
  --p-green-500: #10B981;
  --p-green-600: #059669;
  --p-green-700: #047857;

  /* Cores — Paleta Amber */
  --p-amber-50: #FFFBEB;
  --p-amber-100: #FEF3C7;
  --p-amber-200: #FDE68A;
  --p-amber-300: #FCD34D;
  --p-amber-400: #FBBF24;
  --p-amber-500: #F59E0B;
  --p-amber-600: #D97706;
  --p-amber-700: #B45309;

  /* Cores — Paleta Purple */
  --p-purple-50: #F5F3FF;
  --p-purple-100: #EDE9FE;
  --p-purple-200: #DDD6FE;
  --p-purple-300: #C4B5FD;
  --p-purple-400: #A78BFA;
  --p-purple-500: #8B5CF6;
  --p-purple-600: #7C3AED;
  --p-purple-700: #6D28D9;

  /* Cores — Paleta Indigo (accent) */
  --p-indigo-400: #818CF8;
  --p-indigo-500: #6D4CFF;
  --p-indigo-600: #5A3DE6;

  /* Tipografia — Escalas */
  --p-font-family-sans: 'Inter', system-ui, -apple-system, sans-serif;
  --p-font-family-mono: 'SF Mono', 'Fira Code', 'Consolas', monospace;
  --p-font-family-display: 'Inter', system-ui, -apple-system, sans-serif;

  --p-font-size-xs: 11px;
  --p-font-size-sm: 12px;
  --p-font-size-base: 13px;
  --p-font-size-md: 14px;
  --p-font-size-lg: 16px;
  --p-font-size-xl: 18px;
  --p-font-size-2xl: 20px;
  --p-font-size-3xl: 24px;
  --p-font-size-4xl: 30px;

  --p-font-weight-normal: 400;
  --p-font-weight-medium: 500;
  --p-font-weight-semibold: 600;
  --p-font-weight-bold: 700;

  --p-line-height-tight: 1.25;
  --p-line-height-normal: 1.5;
  --p-line-height-relaxed: 1.6;
  --p-line-height-loose: 1.8;

  --p-letter-spacing-tight: -0.01em;
  --p-letter-spacing-normal: 0;
  --p-letter-spacing-wide: 0.5px;
  --p-letter-spacing-wider: 1px;

  /* Espaçamentos — Escala de 4px */
  --p-space-0: 0px;
  --p-space-1: 4px;
  --p-space-2: 8px;
  --p-space-3: 12px;
  --p-space-4: 16px;
  --p-space-5: 20px;
  --p-space-6: 24px;
  --p-space-8: 32px;
  --p-space-10: 40px;
  --p-space-12: 48px;
  --p-space-16: 64px;

  /* Radius */
  --p-radius-none: 0px;
  --p-radius-sm: 4px;
  --p-radius-md: 8px;
  --p-radius-lg: 12px;
  --p-radius-xl: 16px;
  --p-radius-2xl: 24px;
  --p-radius-full: 9999px;

  /* Sombras */
  --p-shadow-none: none;
  --p-shadow-sm: 0 1px 2px rgba(0,0,0,0.3);
  --p-shadow-md: 0 4px 6px rgba(0,0,0,0.3);
  --p-shadow-lg: 0 10px 15px rgba(0,0,0,0.3);
  --p-shadow-xl: 0 20px 25px rgba(0,0,0,0.3);

  /* Transições / Motion */
  --p-duration-instant: 0s;
  --p-duration-fast: 0.15s;
  --p-duration-normal: 0.2s;
  --p-duration-slow: 0.3s;
  --p-duration-slower: 0.5s;
  --p-easing-default: ease;
  --p-easing-in: ease-in;
  --p-easing-out: ease-out;
  --p-easing-in-out: ease-in-out;

  /* Z-index */
  --p-z-base: 0;
  --p-z-dropdown: 100;
  --p-z-sticky: 200;
  --p-z-overlay: 300;
  --p-z-modal: 400;
  --p-z-popover: 500;
  --p-z-toast: 600;
`;

// ============================================================
// CAMADA 2: TOKENS SEMÂNTICOS (Alias) — variam por tema
// Cada tema define esses valores apontando pra primitivos
// ============================================================

/** Mapa: token semântico → CSS var name */
const SEMANTIC_VAR_MAP: Record<string, string> = {
  // Backgrounds
  bg: '--t-bg',
  bgCard: '--t-bg-card',
  bgCardHover: '--t-bg-card-hover',
  bgInput: '--t-bg-input',
  bgInputHover: '--t-bg-input-hover',
  bgSecondary: '--t-bg-secondary',
  bgOverlay: '--t-bg-overlay',
  bgModal: '--t-bg-modal',
  bgSubtle: '--t-bg-subtle',
  bgHover: '--t-bg-hover',
  bgActive: '--t-bg-active',

  // Borders
  border: '--t-border',
  borderHover: '--t-border-hover',
  borderFocus: '--t-border-focus',
  borderError: '--t-border-error',
  borderSubtle: '--t-border-subtle',

  // Text
  text: '--t-text',
  textSecondary: '--t-text-secondary',
  textMuted: '--t-text-muted',
  textDisabled: '--t-text-disabled',

  // Status / Brand
  primary: '--t-primary',
  primaryHover: '--t-primary-hover',
  primaryBgSubtle: '--t-primary-bg-subtle',
  primaryBg: '--t-primary-bg',
  primaryBgStrong: '--t-primary-bg-strong',
  success: '--t-success',
  successHover: '--t-success-hover',
  successBgSubtle: '--t-success-bg-subtle',
  successBg: '--t-success-bg',
  successBgStrong: '--t-success-bg-strong',
  warning: '--t-warning',
  warningHover: '--t-warning-hover',
  warningBgSubtle: '--t-warning-bg-subtle',
  warningBg: '--t-warning-bg',
  warningBgStrong: '--t-warning-bg-strong',
  danger: '--t-danger',
  dangerHover: '--t-danger-hover',
  dangerBgSubtle: '--t-danger-bg-subtle',
  dangerBg: '--t-danger-bg',
  dangerBgStrong: '--t-danger-bg-strong',
  accent: '--t-accent',
  accentHover: '--t-accent-hover',
  accentBgSubtle: '--t-accent-bg-subtle',
  accentBg: '--t-accent-bg',
  accentBgStrong: '--t-accent-bg-strong',
  purple: '--t-purple',
  purpleHover: '--t-purple-hover',
  purpleBgSubtle: '--t-purple-bg-subtle',
  purpleBg: '--t-purple-bg',
  purpleBgStrong: '--t-purple-bg-strong',

  // Alpha colors
  whiteAlpha2: '--t-white-alpha-2',
  whiteAlpha3: '--t-white-alpha-3',
  whiteAlpha5: '--t-white-alpha-5',
  whiteAlpha8: '--t-white-alpha-8',
  whiteAlpha15: '--t-white-alpha-15',
  whiteAlpha30: '--t-white-alpha-30',
  blackAlpha15: '--t-black-alpha-15',
  blackAlpha30: '--t-black-alpha-30',
  blackAlpha40: '--t-black-alpha-40',
  blackAlpha50: '--t-black-alpha-50',

  // Shadows (por tema)
  shadowSm: '--t-shadow-sm',
  shadowMd: '--t-shadow-md',
  shadowLg: '--t-shadow-lg',
  shadowXl: '--t-shadow-xl',

  // Radius (por tema)
  radiusSm: '--t-radius-sm',
  radiusMd: '--t-radius-md',
  radiusLg: '--t-radius-lg',
  radiusXl: '--t-radius-xl',
  radiusFull: '--t-radius-full',

  // Tipografia (temável)
  fontFamily: '--t-font-family',
  fontMono: '--t-font-family-mono',
  fontDisplay: '--t-font-family-display',
  lineHeightBody: '--t-line-height-body',
  letterSpacingBody: '--t-letter-spacing-body',
};

// ============================================================
// CAMADA 3: TOKENS DE COMPONENTE
// Customização cirúrgica — referencia semânticos por padrão
// ============================================================

const COMPONENT_TOKENS_CSS = `
  /* Button */
  --c-button-bg: var(--t-primary);
  --c-button-bg-hover: var(--t-primary-hover);
  --c-button-text: var(--p-white);
  --c-button-radius: var(--t-radius-md);
  --c-button-font-size: var(--p-font-size-md);
  --c-button-font-weight: var(--p-font-weight-semibold);
  --c-button-padding: var(--p-space-2) var(--p-space-4);
  --c-button-transition: all var(--p-duration-fast) var(--p-easing-default);

  --c-button-secondary-bg: var(--t-bg-hover);
  --c-button-secondary-text: var(--t-text);
  --c-button-danger-bg: var(--t-danger);
  --c-button-danger-text: var(--p-white);
  --c-button-ghost-bg: transparent;
  --c-button-ghost-text: var(--t-text-secondary);

  /* Card */
  --c-card-bg: var(--t-bg-card);
  --c-card-bg-hover: var(--t-bg-card-hover);
  --c-card-border: var(--t-border);
  --c-card-radius: var(--t-radius-lg);
  --c-card-padding: var(--p-space-6);
  --c-card-shadow: var(--t-shadow-sm);

  /* Input */
  --c-input-bg: var(--t-bg-input);
  --c-input-bg-hover: var(--t-bg-input-hover);
  --c-input-border: var(--t-border);
  --c-input-border-focus: var(--t-border-focus);
  --c-input-text: var(--t-text);
  --c-input-placeholder: var(--t-text-muted);
  --c-input-radius: var(--t-radius-md);
  --c-input-font-size: var(--p-font-size-md);
  --c-input-padding: var(--p-space-2) var(--p-space-3);

  /* Modal */
  --c-modal-bg: var(--t-bg-modal);
  --c-modal-overlay: var(--t-bg-overlay);
  --c-modal-radius: var(--t-radius-xl);
  --c-modal-shadow: var(--t-shadow-xl);
  --c-modal-padding: var(--p-space-6);

  /* Badge */
  --c-badge-font-size: var(--p-font-size-xs);
  --c-badge-font-weight: var(--p-font-weight-semibold);
  --c-badge-radius: var(--p-radius-full);
  --c-badge-padding: 2px var(--p-space-2);

  /* Table */
  --c-table-header-bg: var(--t-bg-subtle);
  --c-table-row-hover: var(--t-bg-hover);
  --c-table-border: var(--t-border);
  --c-table-font-size: var(--p-font-size-sm);

  /* Tooltip */
  --c-tooltip-bg: var(--t-bg-modal);
  --c-tooltip-text: var(--t-text);
  --c-tooltip-radius: var(--t-radius-md);
  --c-tooltip-shadow: var(--t-shadow-md);
  --c-tooltip-font-size: var(--p-font-size-sm);

  /* Sidebar */
  --c-sidebar-bg: var(--t-bg-secondary);
  --c-sidebar-width: 260px;
  --c-sidebar-collapsed: 60px;
  --c-sidebar-item-radius: var(--t-radius-md);
  --c-sidebar-item-hover: var(--t-bg-hover);

  /* Header */
  --c-header-height: 56px;
  --c-header-bg: var(--t-bg);
  --c-header-border: var(--t-border);

  /* Toast */
  --c-toast-bg: var(--t-bg-modal);
  --c-toast-text: var(--t-text);
  --c-toast-radius: var(--t-radius-lg);
  --c-toast-shadow: var(--t-shadow-lg);

  /* Dropdown */
  --c-dropdown-bg: var(--t-bg-modal);
  --c-dropdown-border: var(--t-border);
  --c-dropdown-radius: var(--t-radius-lg);
  --c-dropdown-shadow: var(--t-shadow-lg);
  --c-dropdown-item-hover: var(--t-bg-hover);

  /* Tab */
  --c-tab-text: var(--t-text-muted);
  --c-tab-text-active: var(--t-primary);
  --c-tab-indicator: var(--t-primary);
  --c-tab-font-size: var(--p-font-size-md);

  /* Typography — Semântica (temável) */
  --t-font-family: var(--p-font-family-sans);
  --t-font-family-mono: var(--p-font-family-mono);
  --t-font-family-display: var(--p-font-family-display);
  --t-font-size-body: var(--p-font-size-md);
  --t-font-size-small: var(--p-font-size-sm);
  --t-font-size-heading: var(--p-font-size-xl);
  --t-line-height-body: var(--p-line-height-relaxed);
  --t-line-height-heading: var(--p-line-height-tight);
  --t-letter-spacing-body: var(--p-letter-spacing-normal);
  --t-letter-spacing-heading: var(--p-letter-spacing-tight);

  /* Motion — Semântica (temável) */
  --t-transition-fast: var(--p-duration-fast) var(--p-easing-default);
  --t-transition-normal: var(--p-duration-normal) var(--p-easing-default);
  --t-transition-slow: var(--p-duration-slow) var(--p-easing-default);

  /* Spacing — Semântica (temável) */
  --t-space-xs: var(--p-space-1);
  --t-space-sm: var(--p-space-2);
  --t-space-md: var(--p-space-4);
  --t-space-lg: var(--p-space-6);
  --t-space-xl: var(--p-space-8);
  --t-space-2xl: var(--p-space-12);

  /* Layout (temável) */
  --t-max-width: 1400px;
  --t-container-landing: 1200px;
  --t-container-form: 800px;
`;

// ============================================================
// Injeção de CSS no :root (chamada uma vez na inicialização)
// ============================================================

let _stylesInjected = false;

function _injectPrimitives(): void {
  if (typeof document === 'undefined' || _stylesInjected) return;
  const style = document.createElement('style');
  style.id = 'elontools-design-tokens'; // tenant-id: internal CSS element ID
  style.textContent = `:root { ${PRIMITIVES_CSS} ${COMPONENT_TOKENS_CSS} }`;
  document.head.appendChild(style);
  _stylesInjected = true;
}

/** Injeta/atualiza tokens semânticos (Camada 2) no :root — chamada por tema */
export function applyThemeCSS(theme: ThemeColors): void {
  if (typeof document === 'undefined') return;
  _injectPrimitives(); // garante que primitivos + componentes existem

  const root = document.documentElement;
  for (const [key, varName] of Object.entries(SEMANTIC_VAR_MAP)) {
    const value = (theme as any)[key];
    if (value === undefined) continue;
    if (typeof value === 'number') {
      root.style.setProperty(varName, `${value}px`);
    } else {
      root.style.setProperty(varName, value);
    }
  }
}

// Inicializa ao carregar o módulo
function _initThemeCSS(): void {
  if (typeof window === 'undefined') return;
  try {
    const id = localStorage.getItem('wlc-theme');
    const theme = (id && themes[id]) || defaultTheme;
    applyThemeCSS(theme);
  } catch {
    applyThemeCSS(defaultTheme);
  }
}
_initThemeCSS();

// ============================================================
// T — API Pública de Tokens
// 
// Camada 2 (semânticos) = var(--t-*)  → mudam por tema
// Tipografia/espaçamento = valores estáticos (compat com código existente)
// Futuro: migrar gradualmente pra var() também
// ============================================================

export const T = {
  // === CORES (CSS vars — Camada 2 Semântica) ===
  bg: 'var(--t-bg)',
  bgCard: 'var(--t-bg-card)',
  bgCardHover: 'var(--t-bg-card-hover)',
  bgInput: 'var(--t-bg-input)',
  bgInputHover: 'var(--t-bg-input-hover)',
  bgSecondary: 'var(--t-bg-secondary)',
  bgOverlay: 'var(--t-bg-overlay)',
  bgModal: 'var(--t-bg-modal)',
  bgSubtle: 'var(--t-bg-subtle)',
  bgHover: 'var(--t-bg-hover)',
  bgActive: 'var(--t-bg-active)',

  border: 'var(--t-border)',
  borderHover: 'var(--t-border-hover)',
  borderFocus: 'var(--t-border-focus)',
  borderError: 'var(--t-border-error)',
  borderSubtle: 'var(--t-border-subtle)',

  text: 'var(--t-text)',
  textSecondary: 'var(--t-text-secondary)',
  textMuted: 'var(--t-text-muted)',
  textDisabled: 'var(--t-text-disabled)',

  primary: 'var(--t-primary)',
  primaryHover: 'var(--t-primary-hover)',
  primaryBgSubtle: 'var(--t-primary-bg-subtle)',
  primaryBg: 'var(--t-primary-bg)',
  primaryBgStrong: 'var(--t-primary-bg-strong)',
  success: 'var(--t-success)',
  successHover: 'var(--t-success-hover)',
  successBgSubtle: 'var(--t-success-bg-subtle)',
  successBg: 'var(--t-success-bg)',
  successBgStrong: 'var(--t-success-bg-strong)',
  warning: 'var(--t-warning)',
  warningHover: 'var(--t-warning-hover)',
  warningBgSubtle: 'var(--t-warning-bg-subtle)',
  warningBg: 'var(--t-warning-bg)',
  warningBgStrong: 'var(--t-warning-bg-strong)',
  danger: 'var(--t-danger)',
  dangerHover: 'var(--t-danger-hover)',
  dangerBgSubtle: 'var(--t-danger-bg-subtle)',
  dangerBg: 'var(--t-danger-bg)',
  dangerBgStrong: 'var(--t-danger-bg-strong)',
  accent: 'var(--t-accent)',
  accentHover: 'var(--t-accent-hover)',
  accentBgSubtle: 'var(--t-accent-bg-subtle)',
  accentBg: 'var(--t-accent-bg)',
  accentBgStrong: 'var(--t-accent-bg-strong)',
  purple: 'var(--t-purple)',
  purpleHover: 'var(--t-purple-hover)',
  purpleBgSubtle: 'var(--t-purple-bg-subtle)',
  purpleBg: 'var(--t-purple-bg)',
  purpleBgStrong: 'var(--t-purple-bg-strong)',

  // Alpha colors
  whiteAlpha2: 'var(--t-white-alpha-2)',
  whiteAlpha3: 'var(--t-white-alpha-3)',
  whiteAlpha5: 'var(--t-white-alpha-5)',
  whiteAlpha8: 'var(--t-white-alpha-8)',
  whiteAlpha15: 'var(--t-white-alpha-15)',
  whiteAlpha30: 'var(--t-white-alpha-30)',
  blackAlpha15: 'var(--t-black-alpha-15)',
  blackAlpha30: 'var(--t-black-alpha-30)',
  blackAlpha40: 'var(--t-black-alpha-40)',
  blackAlpha50: 'var(--t-black-alpha-50)',

  // Shadows (CSS vars)
  shadowSm: 'var(--t-shadow-sm)',
  shadowMd: 'var(--t-shadow-md)',
  shadowLg: 'var(--t-shadow-lg)',
  shadowXl: 'var(--t-shadow-xl)',

  // Radius (CSS vars)
  radiusSm: 'var(--t-radius-sm)',
  radiusMd: 'var(--t-radius-md)',
  radiusLg: 'var(--t-radius-lg)',
  radiusXl: 'var(--t-radius-xl)',
  radiusFull: 'var(--t-radius-full)',

  // Gradients (brand)
  gradientAccent: 'linear-gradient(135deg, #7C3AED, #2563EB)',
  gradientPrimary: 'linear-gradient(135deg, #2F6BFF, #7c5cfc)',

  // === TIPOGRAFIA (CSS vars — temáveis!) ===
  fontFamily: 'var(--t-font-family)',
  fontMono: 'var(--t-font-family-mono)',
  fontDisplay: 'var(--t-font-family-display)',

  // Font Sizes — valores numéricos (compat com código existente)
  // Migração futura: usar var(--p-font-size-*) diretamente
  textXs: 11,
  textSm: 12,
  textBase: 13,
  textMd: 14,
  textLg: 16,
  textXl: 18,
  text2xl: 20,
  text3xl: 24,
  text4xl: 30,

  // Font Weights
  weightNormal: 400,
  weightMedium: 500,
  weightSemibold: 600,
  weightBold: 700,

  // Line Heights
  lineHeightTight: 'var(--p-line-height-tight)',
  lineHeightNormal: 'var(--p-line-height-normal)',
  lineHeightRelaxed: 'var(--p-line-height-relaxed)',
  lineHeightLoose: 'var(--p-line-height-loose)',

  // Letter Spacing
  letterSpacingTight: 'var(--p-letter-spacing-tight)',
  letterSpacingNormal: 'var(--p-letter-spacing-normal)',
  letterSpacingWide: 'var(--p-letter-spacing-wide)',

  // === ESPAÇAMENTOS (numéricos — compat) ===
  space1: 4,
  space2: 8,
  space3: 12,
  space4: 16,
  space5: 20,
  space6: 24,
  space8: 32,
  space10: 40,
  space12: 48,
  space16: 64,

  // === TRANSIÇÕES (CSS vars — temáveis!) ===
  transitionFast: 'var(--t-transition-fast)',
  transitionNormal: 'var(--t-transition-normal)',
  transitionSlow: 'var(--t-transition-slow)',

  // Motion primitivos (pra uso direto)
  durationFast: 'var(--p-duration-fast)',
  durationNormal: 'var(--p-duration-normal)',
  durationSlow: 'var(--p-duration-slow)',
  easing: 'var(--p-easing-default)',

  // === Z-INDEX ===
  zBase: 'var(--p-z-base)',
  zDropdown: 'var(--p-z-dropdown)',
  zSticky: 'var(--p-z-sticky)',
  zOverlay: 'var(--p-z-overlay)',
  zModal: 'var(--p-z-modal)',
  zPopover: 'var(--p-z-popover)',
  zToast: 'var(--p-z-toast)',

  // === LAYOUT (estático — compat) ===
  maxWidth: 1400,
  containerLanding: 1200,
  containerForm: 800,
  sidebarWidth: 260,
  sidebarCollapsed: 60,
  headerHeight: 56,

  // === BREAKPOINTS (estático) ===
  breakpointXs: '480px',
  breakpointSm: '640px',
  breakpointMd: '768px',
  breakpointLg: '1024px',
  breakpointXl: '1400px',
} as const;

// ============================================================
// Helpers (compat com código existente)
// ============================================================

export const cardStyle = {
  background: T.bgCard,
  border: `1px solid ${T.border}`,
  borderRadius: T.radiusLg,
  padding: T.space6,
} as const;

export const inputStyle = {
  background: T.bgInput,
  border: `1px solid ${T.border}`,
  borderRadius: T.radiusMd,
  color: T.text,
  fontSize: T.textMd,
  padding: '8px 12px',
  outline: 'none',
  width: '100%',
  transition: `border-color ${T.transitionFast}`,
} as const;

export const labelStyle = {
  fontSize: T.textSm,
  fontWeight: T.weightSemibold,
  color: T.textSecondary,
  marginBottom: T.space1,
  display: 'block',
} as const;

// ============================================================
// Component Token helpers (Camada 3)
// Para componentes ui/ que queiram usar tokens de componente
// ============================================================

export const CT = {
  button: {
    bg: 'var(--c-button-bg)',
    bgHover: 'var(--c-button-bg-hover)',
    text: 'var(--c-button-text)',
    radius: 'var(--c-button-radius)',
    fontSize: 'var(--c-button-font-size)',
    fontWeight: 'var(--c-button-font-weight)',
    padding: 'var(--c-button-padding)',
    transition: 'var(--c-button-transition)',
    secondaryBg: 'var(--c-button-secondary-bg)',
    secondaryText: 'var(--c-button-secondary-text)',
    dangerBg: 'var(--c-button-danger-bg)',
    dangerText: 'var(--c-button-danger-text)',
    ghostBg: 'var(--c-button-ghost-bg)',
    ghostText: 'var(--c-button-ghost-text)',
  },
  card: {
    bg: 'var(--c-card-bg)',
    bgHover: 'var(--c-card-bg-hover)',
    border: 'var(--c-card-border)',
    radius: 'var(--c-card-radius)',
    padding: 'var(--c-card-padding)',
    shadow: 'var(--c-card-shadow)',
  },
  input: {
    bg: 'var(--c-input-bg)',
    bgHover: 'var(--c-input-bg-hover)',
    border: 'var(--c-input-border)',
    borderFocus: 'var(--c-input-border-focus)',
    text: 'var(--c-input-text)',
    placeholder: 'var(--c-input-placeholder)',
    radius: 'var(--c-input-radius)',
    fontSize: 'var(--c-input-font-size)',
    padding: 'var(--c-input-padding)',
  },
  modal: {
    bg: 'var(--c-modal-bg)',
    overlay: 'var(--c-modal-overlay)',
    radius: 'var(--c-modal-radius)',
    shadow: 'var(--c-modal-shadow)',
    padding: 'var(--c-modal-padding)',
  },
  badge: {
    fontSize: 'var(--c-badge-font-size)',
    fontWeight: 'var(--c-badge-font-weight)',
    radius: 'var(--c-badge-radius)',
    padding: 'var(--c-badge-padding)',
  },
  table: {
    headerBg: 'var(--c-table-header-bg)',
    rowHover: 'var(--c-table-row-hover)',
    border: 'var(--c-table-border)',
    fontSize: 'var(--c-table-font-size)',
  },
  tooltip: {
    bg: 'var(--c-tooltip-bg)',
    text: 'var(--c-tooltip-text)',
    radius: 'var(--c-tooltip-radius)',
    shadow: 'var(--c-tooltip-shadow)',
    fontSize: 'var(--c-tooltip-font-size)',
  },
  sidebar: {
    bg: 'var(--c-sidebar-bg)',
    width: 'var(--c-sidebar-width)',
    collapsed: 'var(--c-sidebar-collapsed)',
    itemRadius: 'var(--c-sidebar-item-radius)',
    itemHover: 'var(--c-sidebar-item-hover)',
  },
  header: {
    height: 'var(--c-header-height)',
    bg: 'var(--c-header-bg)',
    border: 'var(--c-header-border)',
  },
  toast: {
    bg: 'var(--c-toast-bg)',
    text: 'var(--c-toast-text)',
    radius: 'var(--c-toast-radius)',
    shadow: 'var(--c-toast-shadow)',
  },
  dropdown: {
    bg: 'var(--c-dropdown-bg)',
    border: 'var(--c-dropdown-border)',
    radius: 'var(--c-dropdown-radius)',
    shadow: 'var(--c-dropdown-shadow)',
    itemHover: 'var(--c-dropdown-item-hover)',
  },
  tab: {
    text: 'var(--c-tab-text)',
    textActive: 'var(--c-tab-text-active)',
    indicator: 'var(--c-tab-indicator)',
    fontSize: 'var(--c-tab-font-size)',
  },
} as const;

// NOTA: Não usar destructuring re-export aqui (causa referência circular no bundler)
// Sempre importe { T } from 'tokens' e use T.primary, T.success, etc.
// Para tokens de componente: import { CT } from 'tokens' e use CT.button.bg, etc.
